/**
 * 1 Nov 2026 (D-1102-CREW-3): the crew's phone keeps Arrived and Finish (with their photos) when there is no signal,
 * and sends them when it returns. The rules are in ./queue.ts; this is the glue: the phone's storage, the online /
 * back-to-front / timer triggers, and what the person is told. Off unless Admin → Settings says "The crew can save
 * while offline" — and a switch turned off later does not strand what is already waiting: it still goes.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TxView } from '@shared/api-types';
import { crewOfflineOn } from '@shared/crew-1102';
import { ApiRequestError, apiFetch } from '@/lib/api';
import { useDayPlan } from '@/lib/queries';
import { useTx } from '@/lib/tx';
import { useAnnounce } from '@/components/Announcer';
import { useToast } from '@/components/ui/use-toast';
import { indexedDbStore, memoryStore, OfflineQueue, type QueuedAction, type QueuedPhoto, type QueueStore } from './queue';

type KeepInput = Omit<QueuedAction, 'seq' | 'createdAt' | 'status' | 'attempts' | 'nextTryAt' | 'user'>;

export interface OfflineCtx {
  /** The crew may keep a new press on the phone (the switch is on). */
  enabled: boolean;
  /** This person's actions that are waiting or were refused, in the order they were pressed. */
  items: QueuedAction[];
  /** Lines to show as "Sent ✓" for a few seconds. */
  sentNow: { id: string; text: string }[];
  keep: (input: KeepInput) => Promise<'saved' | 'full' | 'storage' | 'off'>;
  retry: (id: string) => void;
  remove: (id: string) => void;
  flushNow: () => void;
  /** Waiting (not refused) actions for a visit — what the card treats as already done on this phone. */
  waitingFor: (appointmentId: string, kind?: QueuedAction['kind']) => QueuedAction[];
  /** Every action held for a visit, refused ones included. */
  heldFor: (appointmentId: string) => QueuedAction[];
}

const NONE: OfflineCtx = {
  enabled: false,
  items: [],
  sentNow: [],
  keep: async () => 'off',
  retry: () => undefined,
  remove: () => undefined,
  flushNow: () => undefined,
  waitingFor: () => [],
  heldFor: () => [],
};
const Ctx = createContext<OfflineCtx>(NONE);
export const useOfflineQueue = (): OfflineCtx => useContext(Ctx);

const SENT_SHOWN_MS = 10_000;

/** One photo to the visit's folder, with the key that makes a second send of it answer the first one's path. */
async function uploadPhoto(a: QueuedAction, p: QueuedPhoto, key: string): Promise<string> {
  const fd = new FormData();
  fd.set('appointmentId', a.appointmentId);
  fd.set('purpose', p.purpose);
  fd.set('clientKey', key);
  fd.set('file', p.blob!, p.name);
  const res = await fetch('/api/documents', { method: 'POST', body: fd, credentials: 'same-origin', headers: { Accept: 'application/json' } });
  const body = (await res.json().catch(() => null)) as { path?: string; error?: string; message?: string } | null;
  if (!res.ok || !body?.path) throw new ApiRequestError(res.ok ? 500 : res.status, { error: body?.error ?? 'photo', message: body?.message ?? `A photo could not be sent (${res.status}).` });
  return body.path;
}

export function OfflineQueueProvider({ role, userEmail, children }: { role: string | null; userEmail: string | null; children: ReactNode }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const qc = useQueryClient();
  const tx = useTx();
  const isCrew = role === 'crew';
  const user = (userEmail ?? '').trim().toLowerCase();
  const dayPlan = useDayPlan(isCrew);
  const enabled = isCrew && !!user && crewOfflineOn(dayPlan.data?.settings);
  const [version, bump] = useState(0);
  const [sentNow, setSentNow] = useState<{ id: string; text: string }[]>([]);
  const tRef = useRef(t);
  tRef.current = t;
  const txRef = useRef(tx);
  txRef.current = tx;

  const storeRef = useRef<QueueStore | null>(null);
  const queueRef = useRef<OfflineQueue | null>(null);
  if (!queueRef.current) {
    storeRef.current = indexedDbStore() ?? memoryStore();
    queueRef.current = new OfflineQueue({
      store: storeRef.current,
      post: (endpoint, body) => apiFetch(endpoint, { method: 'POST', body: JSON.stringify(body) }),
      upload: uploadPhoto,
      now: () => Date.now(),
      online: () => typeof navigator === 'undefined' || navigator.onLine !== false,
      changed: () => bump((n) => n + 1),
      sent: (a, res) => {
        const text = tRef.current('crew1102.sentOne', { what: tRef.current(`crew1102.what.${a.kind}`, { unit: a.unit }) });
        if (a.kind === 'arrived') {
          const r = res as { transactions?: TxView[]; mock?: boolean } | null;
          const main = r?.transactions?.[r.transactions.length - 1];
          if (main) txRef.current.track({ transaction: main, steps: r!.transactions!, mock: !!r!.mock }, { label: a.label, unit: a.unit, appointmentId: a.appointmentId, dayKey: a.day });
        } else {
          const r = res as { transaction?: TxView; steps?: TxView[]; mock?: boolean } | null;
          if (r?.transaction && r.steps) txRef.current.track({ transaction: r.transaction, steps: r.steps, mock: !!r.mock }, { label: a.label, unit: a.unit, appointmentId: a.appointmentId, dayKey: a.day });
        }
        setSentNow((xs) => [...xs.filter((x) => x.id !== a.id), { id: a.id, text }]);
        window.setTimeout(() => setSentNow((xs) => xs.filter((x) => x.id !== a.id)), SENT_SHOWN_MS);
        toastRef.current(text);
        announceRef.current(text);
        for (const k of ['today', 'attention', 'appointments', 'closeout', 'completions']) void qcRef.current.invalidateQueries({ queryKey: [k] });
      },
    });
  }
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const announceRef = useRef(announce);
  announceRef.current = announce;
  const qcRef = useRef(qc);
  qcRef.current = qc;
  const queue = queueRef.current;

  // load what an earlier visit to the app left on the phone, then keep trying
  useEffect(() => {
    if (!isCrew || !user) return;
    let alive = true;
    void queue.load().then(() => {
      if (alive) void queue.flush(user, true);
    });
    const go = () => void queue.flush(user, true);
    const shown = () => {
      if (document.visibilityState === 'visible') go();
    };
    window.addEventListener('online', go);
    window.addEventListener('pageshow', go);
    document.addEventListener('visibilitychange', shown);
    return () => {
      alive = false;
      window.removeEventListener('online', go);
      window.removeEventListener('pageshow', go);
      document.removeEventListener('visibilitychange', shown);
    };
  }, [isCrew, user, queue]);

  // and by itself, at the back-off times
  // `version` moves whenever the queue changes, which is what makes this a new list
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = useMemo(() => (isCrew ? queue.list(user) : []), [isCrew, queue, user, version]);
  const due = isCrew && user ? queue.nextDue(user) : null;
  useEffect(() => {
    if (due === null || !user) return;
    const h = window.setTimeout(() => void queue.flush(user), Math.max(1_000, due - Date.now()));
    return () => window.clearTimeout(h);
  }, [due, user, queue, items.length]);

  const keep = useCallback<OfflineCtx['keep']>(async (input) => (enabled ? queue.enqueue({ ...input, user }) : 'off'), [enabled, queue, user]);

  const value = useMemo<OfflineCtx>(
    () => ({
      enabled,
      items,
      sentNow,
      keep,
      retry: (id) => void queue.retry(id).then(() => queue.flush(user, true)),
      remove: (id) => void queue.remove(id),
      flushNow: () => void queue.flush(user, true),
      waitingFor: (id, kind) => queue.waitingFor(id, kind).filter((a) => a.user === user),
      heldFor: (id) => queue.heldFor(id).filter((a) => a.user === user),
    }),
    [enabled, items, sentNow, keep, user, queue],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
